import { useApolloClient } from '@apollo/client';
import {
  Block,
  Cancel,
  Delete,
  Edit,
  Save,
  ThumbDown,
  ThumbUp,
  Unarchive
} from '@mui/icons-material';
import { CircularProgress, Grid, Tooltip } from '@mui/material';

import { Formik, FormikProps } from 'formik';
import { Gateway } from 'index';
import { omit } from 'lodash';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import TEST_TAXID_QUERY from 'gql/queries/testTaxId.gql';

import { useCompaniesMutation, useJobmarketCompanyQuery } from 'api/hooks/jobmarket/companies';
import { useJobmarketFacultiesQuery } from 'api/hooks/useJobmarketFacultiesQuery';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import StyledDropzone from 'app/components/Dropzone';
import Logo from 'app/components/Logo';
import { SubtleActionButton } from 'app/components/SubtleActionButton';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { actions as FormErrorActions } from 'app/containers/Dashboard/FormError/slice';
import { translations } from 'locales/i18n';
import { Company, Faculty } from 'types/AppTypes';
import { euCountries } from 'utils/Countries';
import { validateForm } from 'utils/FormErrorHelper';

import { VatCheckResult } from './CreateCompany';
import CompanyDetailBody from './components/CompanyDetailBody';
import { companyValidationSchema } from './validation';

interface Props {
  companyId: number;
  closeHandler: Function;
  noAdmin?: boolean;
  onlyEdit?: boolean;
}

export function UpdateCompany(props: Props) {
  const [editMode, setEditMode] = useState(props.onlyEdit);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [changing, setChanging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    data: company,
    isError: isCompanyError,
    isLoading: isCompanyLoading
  } = useJobmarketCompanyQuery(props.companyId);
  const { update, deletion, updateState } = useCompaniesMutation();

  const { data: faculties, isError } = useJobmarketFacultiesQuery();

  const client = useApolloClient();

  useEffect(() => {
    if (isError || isCompanyError) {
      dispatch(AlertActions.error(`${t(translations.pages.companyDetailPage.errorLoading())}`));
      navigate('/', { replace: true });
    }
  }, [dispatch, isError, isCompanyError, navigate, t]);

  if (!faculties) {
    return <CircularProgress />;
  }

  const isEmailTaken = async value => {
    const { data } = await Gateway.get(`/users/isEmailTaken?userEmail=${value}`);
    return data;
  };

  const handleCancelClick = dirty => {
    if (dirty) {
      setCancelModalOpen(true);
    } else {
      if (props.onlyEdit) {
        props.closeHandler();
      } else {
        setEditMode(false);
        dispatch(FormErrorActions.close());
      }
    }
  };

  const handleSaveClick = async (values, data: Company | undefined) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    const emailTaken = await isEmailTaken(values.mainContact.email);
    if (emailTaken && values.mainContact.email !== data?.mainContact.email) {
      setSubmitting(false);
      return dispatch(AlertActions.error(t(translations.validation.emailTaken())));
    }

    if (values.country !== 'Germany' && euCountries.includes(values.country)) {
      dispatch(AlertActions.success(t(translations.common.validatingTaxId())));
      let dataTestTax: VatCheckResult | null = null;
      try {
        const response = await client.query({
          query: TEST_TAXID_QUERY,
          variables: { taxIdForeignCompany: values.taxId }
        });
        dataTestTax = response.data?.testTaxId as VatCheckResult;
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.common.failedValidatingTaxId(), {
              cause: 'Failed fetching'
            })
          )
        );
        setSubmitting(false);
        return;
      }
      if (dataTestTax) {
        if (!dataTestTax.format_valid) {
          dispatch(
            AlertActions.error(
              t(translations.common.failedValidatingTaxId(), {
                cause: dataTestTax.message
                  ? dataTestTax.message
                  : 'Error while Validating VatNumber'
              })
            )
          );
          setSubmitting(false);
          return;
        }
      }
    }

    let saveBody: any = {
      ...omit(values, [
        '__typename',
        'id',
        'createdAt',
        'active',
        'state',
        'logo',
        'fullAddress',
        'jobOfferings',
        'faculties',
        'userId',
        'blockReason'
      ]),
      courses: values.courses.map(c => c.id)
    };
    if (saveBody.mainContact) {
      saveBody.mainContact = omit(saveBody.mainContact, '__typename' /*, 'id'*/);
    }
    if (values.sameInvoiceaddress) {
      saveBody = omit(saveBody, [
        'invoiceName',
        'invoiceStreet',
        'invoiceCity',
        'invoiceZip',
        'invoiceCountry'
      ]);
    }

    if (values.logo) {
      // Save logo first
      const formData = new FormData();
      formData.append('file', values.logo);
      const { data: fileURI } = await Gateway.post('/upload/logo', formData);
      saveBody.logo = fileURI;
    }

    try {
      await update.mutateAsync({ id: props.companyId, data: saveBody });
      setEditMode(false);
      props.closeHandler();
      dispatch(AlertActions.success(t(translations.pages.manageCompanies.alerts.saveSuccessful())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.manageCompanies.alerts.saveFailed())));
    }
    setSubmitting(false);
  };

  const handleDelete = async (stateHandler: (s: boolean) => void) => {
    if (changing || rejecting || deleting) {
      return;
    }
    stateHandler(true);
    try {
      await deletion.mutateAsync(props.companyId);
      dispatch(
        AlertActions.success(t(translations.pages.manageCompanies.alerts.deleteSuccessful()))
      );
      setEditMode(false);
      props.closeHandler();
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.manageCompanies.alerts.deleteFailed())));
    }
    stateHandler(false);
  };

  const handleStateChange = async (state: string, reason?: string) => {
    if (changing || rejecting || deleting) {
      return;
    }
    setChanging(true);

    try {
      await updateState.mutateAsync({
        id: props.companyId,
        data: {
          state,
          reason: reason ? reason : undefined
        }
      });
      setEditMode(false);
      props.closeHandler();
      dispatch(
        AlertActions.success(t(translations.pages.manageCompanies.alerts.changeStateSuccessful()))
      );
    } catch (err) {
      dispatch(
        AlertActions.error(t(translations.pages.manageCompanies.alerts.changeStateFailed()))
      );
    }
    setChanging(false);
  };

  const determineActions = (data: Company) => {
    switch (data.state) {
      case 'active':
        return (
          <>
            <SubtleActionButton
              slide
              type="default"
              icon={Edit}
              disabled={changing || rejecting || deleting}
              onClick={() => setEditMode(true)}
            >
              {t(translations.common.edit())}
            </SubtleActionButton>
            {!props.noAdmin && (
              <>
                <SubtleActionButton
                  slide
                  type="negative"
                  icon={Block}
                  disabled={changing || rejecting || deleting}
                  loading={changing}
                  onClick={() => setBanModalOpen(true)}
                >
                  {t(translations.common.ban())}
                </SubtleActionButton>
                <SubtleActionButton
                  slide
                  type="negative"
                  icon={Delete}
                  disabled={changing || rejecting || deleting}
                  loading={deleting}
                  onClick={() => setDeleteModalOpen(true)}
                >
                  {t(translations.common.delete())}
                </SubtleActionButton>
              </>
            )}
          </>
        );
      case 'inactive':
        return !props.noAdmin ? (
          <>
            <SubtleActionButton
              slide
              type="positive"
              icon={ThumbUp}
              disabled={changing || rejecting || deleting}
              loading={changing}
              onClick={() => handleStateChange('active')}
            >
              {t(translations.common.unlock())}
            </SubtleActionButton>
            <SubtleActionButton
              slide
              type="negative"
              icon={ThumbDown}
              disabled={changing || rejecting || deleting}
              loading={rejecting}
              onClick={() => setRejectModalOpen(true)}
            >
              {t(translations.common.reject())}
            </SubtleActionButton>
          </>
        ) : null;
      case 'archived':
        return !props.noAdmin ? (
          <>
            <SubtleActionButton
              slide
              type="neutral"
              icon={Unarchive}
              disabled={changing}
              loading={changing}
              onClick={() => handleStateChange('active')}
            >
              {t(translations.common.reactivate())}
            </SubtleActionButton>
          </>
        ) : null;
      default:
        return null;
    }
  };

  const renderDetailView = (
    company: Company,
    faculties: Faculty[],
    formikArgs?: Pick<FormikProps<unknown>, 'setFieldValue' | 'values' | 'errors'>,
    dirty?: boolean,
    isValid?: boolean
  ) => {
    let setFieldValue, values, errors;
    if (editMode) {
      setFieldValue = formikArgs?.setFieldValue;
      values = formikArgs?.values;
      errors = formikArgs?.errors;
    }
    return (
      <div className="company-detail">
        <Helmet>
          <title>{company.name}</title>
        </Helmet>
        <Grid
          className="company-detail__header"
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={2}>
            {editMode ? (
              <StyledDropzone
                name="logo"
                setFieldValue={setFieldValue}
                value={values.logo}
                errors={errors}
                accept={{
                  'image/jpeg': ['.jfif', '.jfif-tbnl', '.jpe', '.jpeg', '.jpg'],
                  'image/png': ['.png', '.x-png'],
                  'image/svg+xml': ['.svg']
                }}
                placeholder={t(translations.pages.manageCompanies.logoUploadPlaceholder())}
              />
            ) : (
              <Logo path="jobmarket/logos" src={company.logo} size="small" />
            )}
          </Grid>
          <Grid item xs={8} style={{ textAlign: 'center' }}>
            {editMode ? (
              <>
                <Tooltip
                  title={t(translations.validation.formHasErrors()) as string}
                  disableHoverListener={isValid}
                  disableFocusListener={isValid}
                  disableTouchListener={isValid}
                >
                  <span>
                    <SubtleActionButton
                      slide
                      type="positive"
                      icon={Save}
                      submit
                      disabled={!isValid || submitting}
                      loading={submitting}
                    >
                      {t(translations.common.save())}
                    </SubtleActionButton>
                  </span>
                </Tooltip>
                <SubtleActionButton
                  slide
                  type="neutral"
                  icon={Cancel}
                  disabled={submitting}
                  onClick={() => {
                    handleCancelClick(dirty);
                  }}
                >
                  {t(translations.common.cancel())}
                </SubtleActionButton>
              </>
            ) : (
              determineActions(company)
            )}
          </Grid>
          <Grid item xs={2} style={{ textAlign: 'end' }}>
            {!editMode && (
              <SubtleActionButton
                slide
                type="neutral"
                icon={Cancel}
                disabled={changing || rejecting || deleting}
                onClick={() => {
                  props.closeHandler();
                }}
              >
                {t(translations.common.cancel())}
              </SubtleActionButton>
            )}
          </Grid>
        </Grid>
        <CompanyDetailBody company={company} faculties={faculties} editMode={editMode} />
        <ConfirmationDialog
          open={cancelModalOpen}
          title={t(translations.pages.manageCompanies.cancelConfirm.title())}
          onClose={() => {
            setCancelModalOpen(false);
          }}
          onConfirm={() => {
            setCancelModalOpen(false);
            if (props.onlyEdit) {
              props.closeHandler();
            } else {
              setEditMode(false);
            }
            dispatch(FormErrorActions.close());
          }}
        >
          <div>{t(translations.pages.manageCompanies.cancelConfirm.message())}</div>
        </ConfirmationDialog>
        <ConfirmationDialog
          open={rejectModalOpen}
          title={t(translations.pages.manageCompanies.rejectConfirm.title())}
          onClose={() => {
            setRejectModalOpen(false);
          }}
          onConfirm={() => {
            setRejectModalOpen(false);
            handleDelete(setRejecting);
          }}
        >
          <div>{t(translations.pages.manageCompanies.rejectConfirm.message())}</div>
        </ConfirmationDialog>
        <ConfirmationDialog
          open={deleteModalOpen}
          title={t(translations.pages.manageCompanies.deleteConfirm.title())}
          onClose={() => {
            setDeleteModalOpen(false);
          }}
          onConfirm={() => {
            setDeleteModalOpen(false);
            handleDelete(setDeleting);
          }}
        >
          <div>{t(translations.pages.manageCompanies.deleteConfirm.message())}</div>
        </ConfirmationDialog>
        <ConfirmationDialog
          open={banModalOpen}
          title={t(translations.pages.companyDetailPage.banCompany.title(), {
            name: company.name
          })}
          onClose={() => {
            setBanModalOpen(false);
          }}
          onConfirm={() => {
            handleStateChange('banned', banReason).then(() => {
              setBanModalOpen(false);
            });
          }}
        >
          <Grid container>
            <Grid container>
              {t(translations.pages.companyDetailPage.banCompany.description())}
            </Grid>
            <ReactQuill
              className="company-detail__body__rte"
              value={banReason}
              onChange={setBanReason}
              onBlur={(prev, source, editor) => {
                const content = editor.getHTML();
                setFieldValue('offerText', content.replace(/<p><br><\/p>/g, ''));
              }}
              theme="snow"
              modules={{
                toolbar: [
                  ['bold', 'italic'],
                  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                  ['clean']
                ],
                clipboard: {
                  allowed: {
                    tags: ['a', 'b', 'strong', 'u', 's', 'i', 'p', 'br', 'ul', 'ol', 'li', 'span'],
                    attributes: ['href', 'rel', 'target', 'class']
                  },
                  keepSelection: true,
                  substituteBlockElements: true,
                  magicPasteLinks: true,
                  matchVisual: false
                }
              }}
              formats={[
                'header',
                'bold',
                'italic',
                'align',
                'size',
                'font',
                'color',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'image'
              ]}
            />
          </Grid>
        </ConfirmationDialog>
      </div>
    );
  };

  if (isCompanyLoading) {
    return <CircularProgress />;
  }

  return editMode ? (
    <Formik
      initialValues={omit({ ...company }, 'logo')}
      onSubmit={values => {
        handleSaveClick(values, company);
      }}
      validate={values => {
        return validateForm(values, companyValidationSchema(t), dispatch);
      }}
    >
      {({ dirty, handleSubmit, setFieldValue, values, errors, isValid }) => {
        return (
          <form onSubmit={handleSubmit}>
            {renderDetailView(
              company!,
              faculties,
              { setFieldValue, values, errors },
              dirty,
              isValid
            )}
          </form>
        );
      }}
    </Formik>
  ) : (
    renderDetailView(company!, faculties)
  );
}
